/* eslint-disable no-undef */
import FilePicker from "components/FilePicker";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import LazyAudioFolderTree from "components/LazyAudioFolderTree";
import { toast } from "react-toastify";
import { Tooltip } from "reactstrap";
import newApi from "service/newApi";
import ModalAttendanceCSVExample from "components/Modal/ModalAttendanceCSVExample";
import EventsWarning, { MAX_EVENTS } from "./EventsWarning";
import { CookieTool } from "utils/CookieTool";

const MAX_FILE_SIZE_MB = +process.env.REACT_APP_MAX_EVENT_FILE_SIZE_IN_MB || 8;
const MAX_CSV_FILES_LIMIT = +process.env.REACT_APP_MAX_CSV_FILES_LIMIT || 1;
const MAX_AUDIO_FILES_LIMIT =
  +process.env.REACT_APP_MAX_AUDIO_FILES_LIMIT || 50;
const allowedEventAudioMimeTypes =
  process.env.REACT_APP_ALLOWED_EVENT_AUDIO_MIME_TYPES ||
  "audio/wave,audio/wav,audio/x-wav,audio/mpeg";

const CONCURRENCY = +process.env.REACT_APP_CONCURRENCY || 6;
const CHUNK_SIZE = +process.env.REACT_APP_CHUNK_SIZE || 8 * 1024 * 1024;
const MAX_RETRIES = +process.env.REACT_APP_MAX_RETRIES || 5;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}
async function backoff(attempt) {
  const base = Math.min(30000, 500 * 2 ** attempt);
  const jitter = Math.floor(Math.random() * base);
  await sleep(jitter);
}

async function probeOffset(sessionUrl, total) {
  const resp = await fetch(sessionUrl, {
    method: "PUT",
    headers: { "Content-Range": `bytes */${total}` },
  });
  if (resp.status === 308) {
    const range = resp.headers.get("Range");
    if (range) {
      const end = Number(range.split("-")[1]);
      return end + 1;
    }
    return 0;
  }
  if (resp.ok) return total;
  throw new Error(`probe failed ${resp.status}`);
}

function putChunk(sessionUrl, blob, start, end, total, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", sessionUrl, true);
    xhr.setRequestHeader("Content-Range", `bytes ${start}-${end}/${total}`);
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(start + e.loaded, total);
      };
    }
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 308 || xhr.status === 200 || xhr.status === 201) {
          resolve(xhr.status);
        } else {
          reject(new Error(`chunk ${xhr.status} ${xhr.responseText || ""}`));
        }
      }
    };
    xhr.onerror = () => reject(new Error("network error"));
    xhr.send(blob);
  });
}

async function uploadResumable(sessionUrl, file, onProgress) {
  const total = file.size;
  let offset = 0;
  try {
    offset = await probeOffset(sessionUrl, total);
  } catch {
    () => 0;
  }

  while (offset < total) {
    const end = Math.min(offset + CHUNK_SIZE, total);
    const blob = file.slice(offset, end);

    let attempt = 0;
    for (;;) {
      try {
        const status = await putChunk(
          sessionUrl,
          blob,
          offset,
          end - 1,
          total,
          onProgress,
        );
        if (status === 308) {
          offset = end;
          onProgress?.(offset, total);
          break;
        }
        onProgress?.(total, total);
        return;
      } catch (e) {
        attempt++;
        if (attempt > MAX_RETRIES) throw e;
        await backoff(attempt);
        try {
          offset = await probeOffset(sessionUrl, total);
        } catch {
          () => 0;
        }
        break;
      }
    }
  }
}

async function runWithLimit(limit, tasks) {
  const q = [...tasks];
  const runners = Array(Math.min(limit, q.length))
    .fill(0)
    .map(async () => {
      while (q.length) {
        const t = q.shift();
        await t();
      }
    });
  await Promise.all(runners);
}

const dataSourceOptions = [
  { value: "csv", label: "Texto (CSV)" },
  { value: "AUDIO", label: "Áudio" },
];

const selectStyles = {
  control: (base) => ({ ...base, fontSize: "14px" }),
  menu: (base) => ({ ...base, fontSize: "14px" }),
};

const SEARCH_PATTERNS = [
  { value: "d-1", label: "d-1 (ontem)" },
  { value: "d-3", label: "d-3 (3 dias atrás)" },
  { value: "d-7", label: "d-7 (uma semana)" },
  { value: "d-14", label: "d-14 (duas semanas)" },
  { value: "d-30", label: "d-30 (trinta dias)" },
];

const FREQUENCY_OPTIONS = [
  { value: "daily", label: "Diariamente", description: "Todo dia" },
  { value: "weekly", label: "Semanal", description: "1x por semana" },
  { value: "monthly", label: "Mensal", description: "1x por mês" },
];

const WEEKDAYS = [
  { value: "seg", label: "S", fullLabel: "Segunda" },
  { value: "ter", label: "T", fullLabel: "Terça" },
  { value: "qua", label: "Q", fullLabel: "Quarta" },
  { value: "qui", label: "Q", fullLabel: "Quinta" },
  { value: "sex", label: "S", fullLabel: "Sexta" },
  { value: "sab", label: "S", fullLabel: "Sábado" },
  { value: "dom", label: "D", fullLabel: "Domingo" },
];

function StepDataSource({ next }) {
  const clientFromToken = CookieTool.get("admin_client_id");
  const clientNameFromToken = CookieTool.get("admin_client");
  const { watch, setValue, control, getValues } = useFormContext();

  const dataSource = watch("data_source");
  const selectedClient = watch("client");
  const dataSourceType = watch("data_source_type");
  const dataSourceFiles = watch("data_source_files") || [];
  const dataSourceFilesMetadata = watch("data_source_files_metadata") || [];
  const folderId = watch("data_source_folder_id");
  const folders = useWatch({ control, name: "folders" }) || [];
  const cachedFoldersMap = useWatch({ control, name: "CachedFolders" });
  const totalEvents = watch("total_events") || 0;
  const isCopy = watch("IsCopy");

  const isRecurring = watch("is_recurring");
  const searchPattern = watch("search_pattern");
  const selectedDays = watch("selected_days") || [
    "seg",
    "ter",
    "qua",
    "qui",
    "sex",
  ];
  const frequency = watch("frequency") || "daily";
  const executionTime = watch("execution_time") || "09:00";
  const recurrenceDayOfMonth = watch("recurrence_day_of_month");

  const [clients, setClients] = useState([]);
  const [stepStatus] = useState("idle"); // eslint-disable-line no-unused-vars
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [copyWarningTooltipOpen, setCopyWarningTooltipOpen] = useState(false);
  const [lastClientId, setLastClientId] = useState(null);

  const [clientLimits, setClientLimits] = useState({
    maxFileSizeBytes: null,
    maxFilesPerFolder: null,
  });

  const [budgetStatus, setBudgetStatus] = useState({
    loading: false,
    available: true,
    percentage: 0,
    alertThreshold: null,
    blocked: false,
    error: null,
  });

  const fetchHierarchyOptions = useCallback((folderIds, cachedMap) => {
    if (!(cachedMap instanceof Map) || cachedMap.size === 0) return [];

    const itemById = new Map();
    const childToParent = new Map();
    for (const [parentId, children] of cachedMap.entries()) {
      for (const child of children) {
        if (child.id) {
          itemById.set(child.id, child);
          childToParent.set(child.id, parentId);
        }
      }
    }

    const nameSet = new Set();
    const selectedFolderIdSet = new Set(folderIds);

    function isDateFolder(item) {
      if (!item) return false;
      if (item.dateFormat != null) return true;
      const name = item.label || item.name || "";
      return /^\d{4}[.-]?\d{2}[.-]?\d{2}(T|\s|$)/.test(name);
    }

    function collectFromDateFolder(dateFolderId) {
      const subFolders = cachedMap.get(dateFolderId) || [];
      subFolders.forEach((f) => {
        if (selectedFolderIdSet.has(f.id)) return;
        const name = f.label || f.name;
        if (name) nameSet.add(name);
      });
    }

    function traverseDown(folderId, depth) {
      if (depth > 5) return false;

      const self = itemById.get(folderId);
      if (self && isDateFolder(self)) {
        collectFromDateFolder(folderId);
        return true;
      }

      const children = cachedMap.get(folderId) || [];
      const dateFolders = children.filter((c) => isDateFolder(c));

      if (dateFolders.length > 0) {
        dateFolders.forEach((df) => collectFromDateFolder(df.id));
        return true;
      }

      let found = false;
      children.forEach((c) => {
        if (cachedMap.has(c.id) && traverseDown(c.id, depth + 1)) found = true;
      });
      return found;
    }

    function findDateFolderAncestor(folderId) {
      let current = folderId;
      for (let i = 0; i < 10; i++) {
        const parentId = childToParent.get(current);
        if (!parentId) return null;
        const parentItem = itemById.get(parentId);
        if (parentItem && isDateFolder(parentItem)) return parentId;
        current = parentId;
      }
      return null;
    }

    for (const folderId of folderIds) {
      const foundDown = traverseDown(folderId, 0);
      if (!foundDown) {
        const ancestorId = findDateFolderAncestor(folderId);
        if (ancestorId) collectFromDateFolder(ancestorId);
      }
    }

    return Array.from(nameSet).map((name) => ({ value: name, label: name }));
  }, []);

  const hierarchyOptions = useMemo(() => {
    if (!isRecurring || dataSource !== "CLOUD" || folders.length === 0)
      return [];
    const cachedMap =
      cachedFoldersMap instanceof Map ? cachedFoldersMap : new Map();
    return fetchHierarchyOptions(folders, cachedMap);
  }, [
    folders,
    isRecurring,
    dataSource,
    cachedFoldersMap,
    fetchHierarchyOptions,
  ]);

  const isDone = !dataSourceType
    ? false
    : dataSource === "LOCAL"
      ? dataSourceFiles.length > 0
      : dataSource === "CLOUD"
        ? folders.length > 0
        : false;

  const checkBudget = async (clientId) => {
    setBudgetStatus((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await newApi.get(
        `/admin/v1/client/${clientId}/spent-budget`,
      );

      const {
        percentage_used = 0,
        alert_threshold = null,
        is_blocked = false,
      } = response.data;

      setBudgetStatus({
        loading: false,
        available: !is_blocked,
        percentage: percentage_used,
        alertThreshold: alert_threshold,
        blocked: is_blocked,
        error: null,
      });
    } catch (error) {
      console.error("Erro ao verificar budget:", error);
      setBudgetStatus((prev) => ({
        ...prev,
        loading: false,
        error: "Erro ao verificar orçamento",
      }));

      toast.warn(
        "Não foi possível verificar o orçamento. Prossiga com cautela.",
      );
    }
  };

  useEffect(() => {
    if (!clientFromToken) {
      newApi
        .get(`/admin/v1/clients?limit=${new Date().getTime()}`)
        .then((response) => {
          setClients(
            response.data.clients.map((client) => ({
              value: client.id,
              label: client.name,
              id: client.id,
              name: client.name,
            })),
          );
        })
        .catch(() => {
          toast.error("Erro ao buscar clientes. Tente novamente mais tarde.");
        });
    } else {
      setValue("client", { id: clientFromToken, name: clientNameFromToken });
    }
  }, [clientFromToken, setValue, clientNameFromToken]);
  useEffect(() => {
    if (selectedClient?.id) {
      const isClientChanging =
        lastClientId && lastClientId !== selectedClient.id;

      if (isClientChanging) {
        setUploadStatus({});
        setUploadProgress({});
        setValue("data_source_files", []);
        setValue("data_source_files_metadata", []);
        setValue("data_source_folder_id", null);
        setValue("csv_event_file_path", null);
        setValue("total_events", 0);
        setValue("data_source", null);
        setValue("data_source_type", null);
        setValue("folders", []);
      }

      checkBudget(selectedClient.id);
      setLastClientId(selectedClient.id);

      newApi
        .get(`/admin/v1/clients/${selectedClient.id}`)
        .then((res) => {
          setClientLimits({
            maxFileSizeBytes: res.data.maxFileSizeBytes,
            maxFilesPerFolder: res.data.maxFilesPerFolder,
          });
        })
        .catch((error) => {
          console.warn("Could not fetch client limits:", error);
          setClientLimits({
            maxFileSizeBytes: null,
            maxFilesPerFolder: null,
          });
        });
    } else {
      setUploadStatus({});
      setUploadProgress({});
      setClientLimits({
        maxFileSizeBytes: null,
        maxFilesPerFolder: null,
      });
      setValue("data_source_files", []);
      setValue("data_source_files_metadata", []);
      setValue("data_source_folder_id", null);
      setValue("csv_event_file_path", null);
      setValue("total_events", 0);
      setValue("data_source", null);
      setValue("data_source_type", null);
      setValue("folders", []);
      setLastClientId(null);
    }
  }, [selectedClient?.id, setValue, lastClientId]);

  const toggleTooltip = () => setTooltipOpen((prev) => !prev);
  const toggleCopyWarningTooltip = () =>
    setCopyWarningTooltipOpen((prev) => !prev);

  const renderBudgetStatus = () => {
    if (!selectedClient) return null;

    if (budgetStatus.loading) {
      return (
        <div className="alert alert-info d-flex align-items-center mb-3">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="sr-only">Verificando orçamento...</span>
          </div>
          Verificando orçamento disponível...
        </div>
      );
    }

    if (budgetStatus.error) {
      return (
        <div className="alert alert-warning mb-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {budgetStatus.error}
        </div>
      );
    }

    if (budgetStatus.blocked) {
      return (
        <div className="alert alert-danger mb-3">
          <i className="fas fa-times-circle me-2"></i>
          <strong>Orçamento Esgotado</strong>
          <br />
          Este cliente atingiu 100% do orçamento disponível. Não é possível
          criar novos pipelines.
        </div>
      );
    }

    if (
      budgetStatus.alertThreshold &&
      budgetStatus.percentage >= budgetStatus.alertThreshold
    ) {
      const alertLevel =
        budgetStatus.percentage >= 90
          ? "danger"
          : budgetStatus.percentage >= 70
            ? "warning"
            : "info";

      return (
        <div className={`alert alert-${alertLevel} mb-3`}>
          <i className="fas fa-exclamation-triangle me-2"></i>
          <strong>Atenção - Orçamento</strong>
          <br />
          Este cliente já utilizou {budgetStatus.percentage}% do orçamento
          disponível.
        </div>
      );
    }
    return null;
  };

  const handleUpload = async (selectedFiles, checked) => {
    if (!selectedClient) {
      toast.error("Selecione um cliente");
      return;
    }
    if (budgetStatus.blocked) {
      toast.error("Não é possível fazer upload. Orçamento esgotado.");
      return;
    }

    const toUpload = selectedFiles.filter(
      ({ id }) => checked.includes(id) && uploadStatus[id] !== "done",
    );
    if (toUpload.length === 0) return;

    const updatedStatus = { ...uploadStatus };
    const updatedProgress = { ...uploadProgress };

    if (dataSourceType === "csv") {
      for (let i = 0; i < toUpload.length; i++) {
        const { id, file } = toUpload[i];
        updatedStatus[id] = "uploading";
        setUploadStatus({ ...updatedStatus });

        const formData = new FormData();
        formData.append("file", file);

        try {
          const params = { clientId: selectedClient.id };
          if (folderId) params.folderId = folderId;

          const response = await newApi.post(
            "/v1/event-texts/upload",
            formData,
            {
              params,
              onUploadProgress: ({ loaded, total }) => {
                updatedProgress[id] = Math.round((loaded * 100) / total);
                setUploadProgress({ ...updatedProgress });
              },
            },
          );

          const newFolderId = response.data.folder_id;
          if (!newFolderId) {
            toast.error("Erro ao criar pasta de arquivos para este cliente.");
            updatedStatus[id] = "error";
          } else {
            if (!folderId) {
              setValue("data_source_folder_id", newFolderId);
              setValue("folders", [
                { id: newFolderId, clientId: selectedClient.id },
              ]);
            }
            if (response.data.file_path)
              setValue("csv_event_file_path", response.data.file_path);
            const meta = dataSourceFilesMetadata || [];
            meta.push({
              id: file.name,
              total_events: response.data.total_events,
            });
            setValue("data_source_files_metadata", meta);
            const currentTotal = getValues("total_events") ?? 0;
            setValue("total_events", currentTotal + response.data.total_events);

            const names = new Set([...(dataSourceFiles || []), file.name]);
            setValue("data_source_files", Array.from(names));
            updatedStatus[id] = "done";
          }
        } catch (err) {
          console.error(err);
          updatedStatus[id] = "error";
          const msg = err?.response?.data?.message;
          if (typeof msg === "string" && msg.includes("Missing headers")) {
            toast.error(
              "Campos obrigatórios não foram incluídos: " +
                msg.replace("Missing headers: ", ""),
            );
          } else {
            toast.error(`Falha ao enviar ${file.name}`);
          }
        }

        setUploadStatus({ ...updatedStatus });
        setUploadProgress({ ...updatedProgress });
      }
      return;
    }

    if (dataSourceType === "AUDIO") {
      const filesPayload = toUpload.map(({ file }) => ({
        filename: file.name,
        size: file.size,
        contentType: file.type,
      }));

      try {
        const currentFolderId = getValues("data_source_folder_id");

        const body = {
          clientId: selectedClient.id,
          files: filesPayload,
        };

        if (currentFolderId) {
          body.folderId = currentFolderId;
        }

        const batchResp = await newApi.post(
          "/v1/event-audios/upload/session",
          body,
        );
        const { folder_id, results } = batchResp.data || {};
        if (!folder_id) throw new Error("Sem folder_id no batch");

        if (!currentFolderId) {
          setValue("data_source_folder_id", folder_id);
          setValue("folders", [{ id: folder_id, clientId: selectedClient.id }]);
        }

        const byFilename = results.reduce((acc, r) => {
          acc[r.filename] = acc[r.filename] || [];
          acc[r.filename].push(r);
          return acc;
        }, {});

        const tasks = [];

        for (const { id, file } of toUpload) {
          updatedStatus[id] = "uploading";
          updatedProgress[id] = 0;

          const arr = byFilename[file.name] || [];
          const resForThis = arr.shift?.();
          byFilename[file.name] = arr;

          if (!resForThis) {
            updatedStatus[id] = "error";
            toast.error(`Falha ao criar sessão para ${file.name}`);
            continue;
          }
          if (resForThis.error) {
            updatedStatus[id] = "error";
            const message =
              resForThis.error === "FILE_TOO_LARGE"
                ? "Arquivo maior que o limite"
                : resForThis.error === "MIME_NOT_ALLOWED"
                  ? "Tipo de arquivo não permitido"
                  : resForThis.error === "DUPLICATE_IN_BATCH"
                    ? "Nome duplicado no lote"
                    : resForThis.error === "MAX_FILES_PER_FOLDER_REACHED"
                      ? "Limite de 50 arquivos atingido"
                      : "Não foi possível iniciar o upload";
            toast.error(`${file.name}: ${message}`);
            continue;
          }

          const { session_url, object_path } = resForThis;
          if (!session_url || !object_path) {
            updatedStatus[id] = "error";
            toast.error(`Sessão inválida para ${file.name}`);
            continue;
          }

          tasks.push(async () => {
            try {
              await uploadResumable(session_url, file, (sent, total) => {
                updatedProgress[id] = Math.round((sent * 100) / total);
                setUploadProgress({ ...updatedProgress });
              });

              await newApi.post("/v1/event-audios/upload/session/complete", {
                clientId: selectedClient.id,
                folderId: folder_id,
                filename: file.name,
                objectPath: object_path,
              });

              updatedStatus[id] = "done";
              setUploadStatus({ ...updatedStatus });
            } catch (err) {
              console.error(err);
              updatedStatus[id] = "error";
              setUploadStatus({ ...updatedStatus });
              toast.error(`Falha ao enviar ${file.name}`);
            }
          });
        }

        setUploadStatus({ ...updatedStatus });
        setUploadProgress({ ...updatedProgress });

        await runWithLimit(CONCURRENCY, tasks);

        const successfulUploads = toUpload
          .filter(({ id }) => updatedStatus[id] === "done")
          .map(({ file }) => file.name);

        if (successfulUploads.length > 0) {
          const names = new Set([
            ...(dataSourceFiles || []),
            ...successfulUploads,
          ]);
          setValue("data_source_files", Array.from(names));
        }
      } catch (e) {
        console.error(e);
        toast.error("Erro ao criar sessões em lote. Tente novamente.");
      }
    }
  };

  const handleRemoveFiles = (
    updatedFiles,
    removedFileName,
    isUploadedFile = false,
  ) => {
    if (isUploadedFile) {
      const updatedUploadedFiles = Array.isArray(updatedFiles)
        ? updatedFiles
        : (getValues("data_source_files") || []).filter(
            (fileName) => fileName !== removedFileName,
          );
      setValue("data_source_files", updatedUploadedFiles);

      const currentMetadata = getValues("data_source_files_metadata") || [];
      const updatedMetadata = currentMetadata.filter(
        (meta) => meta.id !== removedFileName,
      );
      setValue("data_source_files_metadata", updatedMetadata);

      const newTotalEvents = updatedMetadata.reduce(
        (sum, meta) => sum + (meta.total_events || 0),
        0,
      );
      setValue("total_events", newTotalEvents);
    }
  };

  const isRecurringIncomplete =
    isRecurring &&
    (!searchPattern || (frequency === "monthly" && !recurrenceDayOfMonth));

  const handleNext = () => {
    if (budgetStatus.blocked) {
      toast.error("Não é possível prosseguir. Orçamento esgotado.");
      return;
    }
    next();
  };

  const handleDaysToggle = (day) => {
    const current = getValues("selected_days") || [];
    if (current.includes(day)) {
      if (current.length === 1) return;
      setValue(
        "selected_days",
        current.filter((d) => d !== day),
      );
    } else {
      setValue("selected_days", [...current, day]);
    }
  };

  const getScheduleDescription = () => {
    const freqLabel =
      FREQUENCY_OPTIONS.find((f) => f.value === frequency)?.label || "";
    const time = executionTime || "09:00";

    if (frequency === "daily") {
      return `Executa todos os dias às ${time}`;
    }

    if (frequency === "weekly") {
      const daysStr = WEEKDAYS.filter((w) =>
        (selectedDays || []).includes(w.value),
      )
        .map((w) => w.fullLabel)
        .join(", ");
      return `Executa ${freqLabel.toLowerCase()} (${daysStr}) às ${time}`;
    }

    if (frequency === "monthly") {
      const day = recurrenceDayOfMonth || "?";
      return `Executa no dia ${day} de cada mês às ${time}`;
    }

    return `Executa ${freqLabel.toLowerCase()} às ${time}`;
  };

  return (
    <div className="card bg-secondary shadow">
      <div className="card-header bg-white border-0">
        <h3 className="mb-0">Fonte de dados</h3>
      </div>
      <div className="card-body">
        <form>
          {!clientFromToken && (
            <div className="mb-3">
              <label className="form-control-label">Cliente</label>
              <Controller
                name="client"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={clients}
                    isLoading={clients.length === 0}
                    placeholder="Selecione um cliente"
                    onChange={(option) =>
                      field.onChange(
                        option ? { id: option.id, name: option.name } : null,
                      )
                    }
                    value={
                      clients.find((opt) => opt.id === field.value?.id) || null
                    }
                    isClearable
                  />
                )}
              />
            </div>
          )}
          {renderBudgetStatus()}

          {selectedClient && !budgetStatus.loading && (
            <>
              <div className="mb-3 d-flex gap-2">
                {["LOCAL", "CLOUD"].map((type) => (
                  <button
                    key={type}
                    type="button"
                    className={`btn ${
                      dataSource === type
                        ? "btn-primary"
                        : "btn-outline-primary"
                    }`}
                    onClick={() => {
                      setValue("data_source", type);
                      setValue("data_source_type", null);
                    }}
                  >
                    {type === "LOCAL" ? "Meu Computador" : "Nuvem Concentrix"}
                  </button>
                ))}
              </div>

              {dataSource && (
                <div className="mb-3">
                  <label className="form-control-label">
                    Tipo de dados{" "}
                    <i className="fas fa-info-circle" id="dataInfo" />
                    {isCopy && (
                      <i
                        className="fas fa-exclamation-triangle text-warning ml-2"
                        id="copyWarning"
                        style={{ cursor: "pointer" }}
                      />
                    )}
                    <Tooltip
                      placement="right"
                      isOpen={tooltipOpen}
                      target="dataInfo"
                      toggle={toggleTooltip}
                    >
                      <div>
                        Limite CSV: {MAX_CSV_FILES_LIMIT} arquivo(s) <br />
                        Limite áudio: {MAX_AUDIO_FILES_LIMIT} arquivo(s) <br />
                        Tamanho máximo: {MAX_FILE_SIZE_MB} MB
                      </div>
                    </Tooltip>
                    {isCopy && (
                      <Tooltip
                        placement="right"
                        isOpen={copyWarningTooltipOpen}
                        target="copyWarning"
                        toggle={toggleCopyWarningTooltip}
                      >
                        <div>
                          <strong>⚠️ Cópia de Pipeline</strong>
                          <br />
                          {dataSource === "LOCAL" ? (
                            <>
                              Esta é uma cópia - os arquivos originais não foram
                              incluídos.
                              <br />
                              Você precisa adicionar novos arquivos para
                              continuar.
                            </>
                          ) : (
                            <>
                              Esta é uma cópia - verifique se as pastas estão
                              corretas.
                              <br />
                              Ajuste a seleção se necessário.
                            </>
                          )}
                        </div>
                      </Tooltip>
                    )}
                  </label>
                  <Controller
                    name="data_source_type"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={dataSourceOptions}
                        placeholder="Selecione o tipo"
                        onChange={(option) =>
                          field.onChange(option ? option.value : "")
                        }
                        value={
                          dataSourceOptions.find(
                            (opt) => opt.value === field.value,
                          ) || null
                        }
                        isDisabled={dataSourceFiles.length > 0}
                        styles={selectStyles}
                        isClearable
                      />
                    )}
                  />
                </div>
              )}

              {dataSource === "CLOUD" && dataSourceType && (
                <div className="mb-3">
                  <label className="form-control-label">
                    Selecione as pastas
                  </label>
                  <Controller
                    name="folders"
                    control={control}
                    render={({ field }) => (
                      <LazyAudioFolderTree
                        isMulti={true}
                        selectedItems={field.value}
                      />
                    )}
                  />
                </div>
              )}

              {dataSource === "LOCAL" && dataSourceType === "csv" && (
                <ModalAttendanceCSVExample />
              )}

              {dataSource === "LOCAL" && dataSourceType === "AUDIO" && (
                <div className="mb-3">
                  <label className="form-control-label">
                    Arquivos de áudio
                  </label>
                  <FilePicker
                    multiple
                    accept={allowedEventAudioMimeTypes}
                    uploadedFiles={dataSourceFiles}
                    onUpload={handleUpload}
                    onFilesRemoved={handleRemoveFiles}
                    uploadProgress={uploadProgress}
                    uploadStatus={uploadStatus}
                    maxFiles={
                      clientLimits.maxFilesPerFolder || MAX_AUDIO_FILES_LIMIT
                    }
                    maxFileSizeInMB={
                      clientLimits.maxFileSizeBytes
                        ? clientLimits.maxFileSizeBytes / (1024 * 1024)
                        : MAX_FILE_SIZE_MB
                    }
                    folderId={folderId}
                    clientId={selectedClient.id}
                  />
                </div>
              )}

              {dataSource === "LOCAL" && dataSourceType === "csv" && (
                <div className="mb-3">
                  <label className="form-control-label">
                    Arquivos de texto
                  </label>
                  <FilePicker
                    multiple
                    accept="text/csv"
                    uploadedFiles={dataSourceFiles}
                    onUpload={handleUpload}
                    onFilesRemoved={handleRemoveFiles}
                    uploadProgress={uploadProgress}
                    uploadStatus={uploadStatus}
                    maxFiles={
                      clientLimits.maxFilesPerFolder || MAX_CSV_FILES_LIMIT
                    }
                    maxFileSizeInMB={
                      clientLimits.maxFileSizeBytes
                        ? clientLimits.maxFileSizeBytes / (1024 * 1024)
                        : MAX_FILE_SIZE_MB
                    }
                    folderId={folderId}
                    clientId={selectedClient.id}
                  />
                </div>
              )}

              {totalEvents > MAX_EVENTS ? <EventsWarning /> : ""}

              {isDone && (
                <>
                  <div className="card border mb-3">
                    <div className="card-body py-3 d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <i className="far fa-calendar-alt fa-lg mr-3 text-primary" />
                        <div>
                          <p className="font-weight-bold mb-0">
                            Tornar recorrente?
                          </p>
                          <p className="text-muted mb-0 small">
                            Agende execuções automáticas
                          </p>
                        </div>
                      </div>
                      <Controller
                        name="is_recurring"
                        control={control}
                        render={({ field }) => (
                          <div className="custom-control custom-switch">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="is_recurring_toggle"
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="is_recurring_toggle"
                            />
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  {isRecurring && (
                    <div className="card border mb-3">
                      <div className="card-body">
                        <div className="mb-3">
                          <label className="form-control-label">
                            Padrão de busca
                          </label>
                          <Controller
                            name="search_pattern"
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={SEARCH_PATTERNS}
                                styles={selectStyles}
                                value={
                                  SEARCH_PATTERNS.find(
                                    (p) => p.value === field.value,
                                  ) || null
                                }
                                onChange={(opt) =>
                                  field.onChange(opt ? opt.value : null)
                                }
                              />
                            )}
                          />
                        </div>

                        {dataSource === "CLOUD" && folders.length > 0 && (
                          <div className="mb-3">
                            <label className="form-control-label">
                              <i className="fas fa-filter mr-1" />
                              Filtrar por{" "}
                              <span className="text-muted font-weight-normal">
                                (opcional)
                              </span>
                            </label>
                            <Controller
                              name="recurrence_filters"
                              control={control}
                              render={({ field }) => (
                                <>
                                  <CreatableSelect
                                    {...field}
                                    isMulti
                                    options={hierarchyOptions}
                                    placeholder="Selecione ou digite para adicionar..."
                                    styles={selectStyles}
                                    noOptionsMessage={() =>
                                      "Sem opções disponíveis"
                                    }
                                    value={(field.value || []).map((v) =>
                                      typeof v === "string"
                                        ? { value: v, label: v }
                                        : v,
                                    )}
                                    onChange={(opts) =>
                                      field.onChange(
                                        (opts || []).map((o) => o.value),
                                      )
                                    }
                                  />
                                </>
                              )}
                            />
                          </div>
                        )}

                        <div className="mb-3">
                          <label className="form-control-label d-flex align-items-center">
                            <i className="fas fa-sync-alt mr-2" />
                            Frequência da análise
                          </label>
                          <div
                            className="row no-gutters"
                            style={{ gap: "0.5rem" }}
                          >
                            {FREQUENCY_OPTIONS.map((opt) => {
                              const isSelected = frequency === opt.value;
                              return (
                                <div
                                  key={opt.value}
                                  className="col"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    setValue("frequency", opt.value)
                                  }
                                  onKeyDown={(e) =>
                                    e.key === "Enter" &&
                                    setValue("frequency", opt.value)
                                  }
                                  style={{
                                    border: `1px solid ${isSelected ? "#5e72e4" : "#dee2e6"}`,
                                    borderRadius: "0.375rem",
                                    padding: "0.75rem 0.5rem",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: isSelected
                                      ? "rgba(94, 114, 228, 0.1)"
                                      : "#fff",
                                    outline: "none",
                                    minWidth: 0,
                                  }}
                                >
                                  <i
                                    className={`far fa-calendar${opt.value === "daily" ? "-alt" : opt.value === "weekly" ? "" : "-check"} fa-lg mb-2 d-block`}
                                    style={{
                                      color: isSelected ? "#5e72e4" : "#94a3b8",
                                    }}
                                  />
                                  <p
                                    className="mb-0 font-weight-bold"
                                    style={{
                                      fontSize: "13px",
                                      color: isSelected ? "#5e72e4" : "#212529",
                                    }}
                                  >
                                    {opt.label}
                                  </p>
                                  <p
                                    className="mb-0 text-muted"
                                    style={{ fontSize: "11px" }}
                                  >
                                    {opt.description}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {frequency === "weekly" && (
                          <div className="mb-3">
                            <label className="form-control-label d-flex align-items-center">
                              <i className="far fa-calendar-alt mr-2" />
                              Dias da semana
                            </label>
                            <div
                              className="d-flex flex-wrap"
                              style={{ gap: "0.375rem" }}
                            >
                              {WEEKDAYS.map((day) => {
                                const isActive = (selectedDays || []).includes(
                                  day.value,
                                );
                                return (
                                  <button
                                    key={day.value}
                                    type="button"
                                    title={day.fullLabel}
                                    onClick={() => handleDaysToggle(day.value)}
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "50%",
                                      border: `1px solid ${isActive ? "#5e72e4" : "#dee2e6"}`,
                                      backgroundColor: isActive
                                        ? "#5e72e4"
                                        : "#fff",
                                      color: isActive ? "#fff" : "#212529",
                                      fontWeight: "600",
                                      fontSize: "13px",
                                      cursor: "pointer",
                                      padding: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      lineHeight: 1,
                                    }}
                                  >
                                    {day.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {frequency === "monthly" && (
                          <div className="mb-3">
                            <label className="form-control-label d-flex align-items-center">
                              <i className="far fa-calendar-check mr-2" />
                              Dia do mês
                            </label>
                            <Controller
                              name="recurrence_day_of_month"
                              control={control}
                              render={({ field }) => (
                                <input
                                  {...field}
                                  value={field.value ?? ""}
                                  type="number"
                                  className="form-control"
                                  min={1}
                                  max={31}
                                  placeholder="Ex: 1"
                                  style={{
                                    maxWidth: "200px",
                                    color: "#212529",
                                  }}
                                  onChange={(e) => {
                                    const val = parseInt(e.target.value, 10);
                                    field.onChange(
                                      !isNaN(val) && val >= 1 && val <= 31
                                        ? val
                                        : null,
                                    );
                                  }}
                                />
                              )}
                            />
                            <small className="form-text text-muted">
                              Dia do mês em que a pipeline será executada (1–31)
                            </small>
                          </div>
                        )}

                        <div className="mb-3">
                          <label className="form-control-label d-flex align-items-center">
                            <i className="far fa-clock mr-2" />
                            Horário de execução
                          </label>
                          <Controller
                            name="execution_time"
                            control={control}
                            render={({ field }) => (
                              <input
                                {...field}
                                type="time"
                                className="form-control"
                                step="900"
                                style={{ maxWidth: "200px", color: "#212529" }}
                              />
                            )}
                          />
                        </div>

                        <div
                          className="p-3 rounded"
                          style={{
                            backgroundColor: "rgba(94, 114, 228, 0.08)",
                            border: "1px dashed rgba(94, 114, 228, 0.4)",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <span
                              className="mr-2 rounded-circle d-inline-block flex-shrink-0"
                              style={{
                                width: "8px",
                                height: "8px",
                                backgroundColor: "#5e72e4",
                                marginTop: "1px",
                              }}
                            />
                            <span
                              className="font-weight-bold"
                              style={{ fontSize: "14px" }}
                            >
                              {getScheduleDescription()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleNext}
                  title={
                    budgetStatus.blocked
                      ? "Orçamento esgotado"
                      : stepStatus === "done"
                        ? "Seguir para o próximo passo"
                        : "Complete este passo corretamente"
                  }
                  disabled={
                    !isDone || budgetStatus.blocked || isRecurringIncomplete
                  }
                >
                  Próximo
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default StepDataSource;
